<template>
  <div class="safe-youtube-container w-full h-full relative">
    <iframe
      v-if="useProxy"
      ref="iframeRef"
      :src="proxySrc"
      class="w-full h-full border-0 rounded-lg"
      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
      allowfullscreen
      referrerpolicy="strict-origin-when-cross-origin"
    ></iframe>
    <div v-else ref="containerRef" class="w-full h-full"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, defineProps, defineEmits, defineExpose } from 'vue';
import { Capacitor } from '@capacitor/core';
import youtubePlayerFactory from 'youtube-player';

const props = defineProps({
  videoid: {
    type: String,
    required: true
  },
  autoplay: {
    type: Number,
    default: 1
  },
  loop: {
    type: Number,
    default: 1
  },
  controls: {
    type: Number,
    default: 0
  },
  width: {
    type: [Number, String],
    default: 480
  },
  height: {
    type: [Number, String],
    default: 320
  },
  // Tempo inicial (s): permite retomar sem começar do 0 (handoff entre hosts).
  startSeconds: {
    type: Number,
    default: 0
  }
});

const emits = defineEmits(['ended', 'paused', 'played', 'ready']);

const containerRef = ref(null);
const iframeRef = ref(null);
let playerInstance = null;
let isUnmounted = false;

const isIos = Capacitor.getPlatform() === 'ios';
const useProxy = ref(isIos);

const internalCurrentTime = ref(0);
const internalDuration = ref(0);

const proxyBase = process.env.VUE_APP_YOUTUBE_PROXY_URL || 'https://live.triadmusic.app/embed.html';

const proxySrc = computed(() => {
  if (!props.videoid) return '';
  const params = new URLSearchParams({
    v: props.videoid,
    autoplay: String(props.autoplay),
    controls: String(props.controls),
    loop: String(props.loop)
  });
  if (props.startSeconds > 0) {
    params.set('start', String(Math.floor(props.startSeconds)));
  }
  return `${proxyBase}?${params.toString()}`;
});

function postToIframe(message) {
  try {
    if (iframeRef.value && iframeRef.value.contentWindow) {
      iframeRef.value.contentWindow.postMessage(message, '*');
    }
  } catch (err) {
    console.warn('[SafeYoutube] Failed to post message to proxy iframe:', err);
  }
}

function handleIframeMessage(event) {
  if (isUnmounted || !event.data || typeof event.data !== 'object') return;
  const data = event.data;

  if (data.type === 'ready') {
    if (props.startSeconds > 0) {
      postToIframe({ action: 'seekTo', seconds: props.startSeconds, allowSeekAhead: true });
    }
    emits('ready');
  } else if (data.type === 'stateChange') {
    if (data.currentTime !== undefined) internalCurrentTime.value = data.currentTime;
    if (data.duration !== undefined) internalDuration.value = data.duration;

    // YouTube state: 0 = ended, 1 = playing, 2 = paused
    if (data.state === 0) {
      emits('ended');
    } else if (data.state === 2) {
      emits('paused');
    } else if (data.state === 1) {
      emits('played');
    }
  } else if (data.type === 'timeUpdate') {
    if (data.currentTime !== undefined) internalCurrentTime.value = data.currentTime;
    if (data.duration !== undefined) internalDuration.value = data.duration;
  }
}

const proxyPlayerApi = {
  playVideo: () => postToIframe({ action: 'play' }),
  pauseVideo: () => postToIframe({ action: 'pause' }),
  stopVideo: () => postToIframe({ action: 'stop' }),
  seekTo: (seconds, allowSeekAhead = true) => {
    internalCurrentTime.value = seconds;
    postToIframe({ action: 'seekTo', seconds, allowSeekAhead });
  },
  loadVideoById: (id) => {
    postToIframe({ action: 'loadVideo', videoId: id });
  },
  getCurrentTime: async () => internalCurrentTime.value,
  getDuration: async () => internalDuration.value,
  destroy: () => {}
};

onMounted(() => {
  if (useProxy.value) {
    window.addEventListener('message', handleIframeMessage);
    return;
  }

  if (!containerRef.value || !props.videoid) return;

  const playerVars = {
    autoplay: props.autoplay,
    loop: props.loop,
    controls: props.controls,
    modestbranding: 1,
    playlist: props.videoid,
    playsinline: 1
  };
  if (props.startSeconds > 0) {
    playerVars.start = Math.floor(props.startSeconds);
  }

  try {
    playerInstance = youtubePlayerFactory(containerRef.value, {
      host: 'https://www.youtube-nocookie.com',
      width: props.width,
      height: props.height,
      videoId: props.videoid,
      playerVars
    });

    playerInstance.on('ready', (e) => {
      if (isUnmounted) return;
      if (props.startSeconds > 0) {
        try { playerInstance.seekTo(props.startSeconds, true); } catch (err) { /* ignore */ }
      }
      emits('ready', e);
    });

    playerInstance.on('stateChange', (e) => {
      if (isUnmounted) return;
      const YTState = window.YT?.PlayerState;
      if (e.data === (YTState?.ENDED ?? 0)) {
        emits('ended');
      } else if (e.data === (YTState?.PAUSED ?? 2)) {
        emits('paused');
      } else if (e.data === (YTState?.PLAYING ?? 1)) {
        emits('played');
      }
    });
  } catch (err) {
    console.error('[SafeYoutube] Error initializing player:', err);
  }
});

onUnmounted(() => {
  isUnmounted = true;
  if (useProxy.value) {
    window.removeEventListener('message', handleIframeMessage);
  }
  if (playerInstance) {
    try {
      if (typeof playerInstance.destroy === 'function') {
        playerInstance.destroy();
      }
    } catch (e) {
      // Safe guard against race conditions or destroyed DOM elements
    }
    playerInstance = null;
  }
});

watch(() => props.videoid, (newId) => {
  if (useProxy.value) {
    postToIframe({ action: 'loadVideo', videoId: newId });
    if (props.autoplay) {
      postToIframe({ action: 'play' });
    }
    return;
  }
  if (playerInstance && newId && !isUnmounted) {
    try {
      playerInstance.loadVideoById(newId);
      if (props.autoplay) {
        playerInstance.playVideo();
      }
    } catch (e) {
      console.warn('[SafeYoutube] Error loading video by id:', e);
    }
  }
});

// Expose safe player API proxy
defineExpose({
  get player() {
    return useProxy.value ? proxyPlayerApi : playerInstance;
  }
});
</script>
